import { useState } from 'react'
import { Link } from 'react-router-dom'
import BookingModal from '../../components/BookingModal'
import FontAwesomeIcon from '../../components/FontAwesomeIcon'
import FAQSection from '../../components/FAQSection'

export default function GorillaHabituation() {
  const [isModalOpen, setIsModalOpen] = useState(false)

  const faqs = [
    {
      question: "How is habituation different from standard gorilla trekking?",
      answer: "Standard trekking gives you 1 hour with fully habituated gorillas. Habituation gives you 4 hours with semi-wild families still being trained. You work alongside researchers, learn habituation techniques, and witness behaviors like morning wake-ups and nest building. Group size is smaller (4 versus 8), the experience is more educational, and you get deeper insight into gorilla lives and conservation science."
    },
    {
      question: "Do I need to be extremely fit?",
      answer: "Yes. Habituation is more physically demanding than standard trekking. Expect 6-8 hours of hiking through steep, muddy, heavily vegetated terrain at altitude. You will scramble through vines, climb slopes, and move constantly with the gorillas. If you cannot hike for several hours with  a daypack, this may not be for you. However, if you are reasonably fit and determined, the challenge is absolutely manageable and worth it."
    },
    {
      question: "Is it worth the $1,500 permit cost?",
      answer: "If you want the deepest possible gorilla experience and are genuinely interested in conservation, absolutely. You get 4 times more gorilla contact, work with researchers, witness unique behaviors, and enjoy a more exclusive experience (4 people versus 8). Wildlife photographers and serious nature enthusiasts find it invaluable. The permit fee is higher than standard trekking, but you're getting four times the gorilla time plus the research experience. Contact us to discuss current permit costs."
    },
    {
      question: "Where does habituation take place?",
      answer: "Only in the Rushaga sector of Bwindi Impenetrable National Park. This southern sector has the most gorilla families (8) and is where new habituation projects occur. You will need to stay in or near Rushaga—Nkuringo, Buhoma, and Ruhija sectors do not offer habituation experiences."
    },
    {
      question: "Can I do both habituation and standard trekking?",
      answer: "Yes, and many serious gorilla enthusiasts do exactly this! Habituation shows you the science and deeper behavioral patterns, while standard trekking lets you compare experiences with fully habituated versus semi-wild families. Doing both gives you the complete gorilla tourism spectrum. Budget several days for recovery—these treks are exhausting!"
    },
    {
      question: "What is the success rate?",
      answer: "Very high—95%+. Researchers track families daily and know exactly where they are. Because habituation families are still being trained, they move less than fully habituated groups, making them somewhat easier to locate. The bigger variables are trekking duration (highly unpredictable) and weather (which can extend hiking times)."
    },
    {
      question: "Are habituation families more dangerous?",
      answer: "Not dangerous, but potentially more unpredictable. Semi-wild gorillas may display defensive behaviors like bluff charges or vocalizations to test boundaries. This is normal habituation behavior. Your experienced researchers and armed rangers know exactly how to read these signals and keep everyone safe. Follow instructions carefully and you will be fine. Many visitors find this wildness thrilling—it feels more authentic than fully habituated families."
    },
    {
      question: "How far in advance should I book?",
      answer: "At least 6-12 months, especially for dry season (June-September, December-February). Only 4 permits are available per day, making habituation much more limited than standard trekking. We can check real-time availability and secure permits on your behalf."
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section
        className="relative h-96 md:h-[500px] bg-cover bg-center flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(https://images.unsplash.com/photo-1551033406-611cf9a28f67?w=1600)',
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl text-white">
            <h1 className="text-3xl md:text-5xl font-bold mb-3 font-heading">
              Gorilla Habituation Experience
            </h1>
            <p className="text-xl md:text-2xl mb-6">
              Spend 4 hours with researchers training wild gorillas—the ultimate primate encounter
            </p>
            <div className="flex items-center gap-6 mb-6">
              <div className="flex items-center gap-2">
                <div className="flex text-safari-gold">
                  {[...Array(5)].map((_, i) => (
                    <FontAwesomeIcon key={i} icon="fa-star" className="w-5 h-5" />
                  ))}
                </div>
                <span className="text-lg">(95+ reviews)</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-4 mb-8">
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-shield-halved" className="w-4 h-4 mr-2" />
                UWA Licensed
              </div>
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-microscope" className="w-4 h-4 mr-2" />
                Research Experience
              </div>
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-star" className="w-4 h-4 mr-2" />
                Exclusive Access
              </div>
            </div>
            <button
              onClick={() => setIsModalOpen(true)}
              className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Book This Exclusive Experience
            </button>
          </div>
        </div>
      </section>

      {/* Page Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="lg:grid lg:grid-cols-3 lg:gap-12">
          {/* Main Content */}
          <main className="lg:col-span-2">
            {/* Full Activity Description */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8">
                The Most Intimate Gorilla Experience on Earth
              </h2>

              <div className="prose prose-lg max-w-none">
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  While standard gorilla trekking offers one magical hour with habituated gorillas, the Gorilla Habituation Experience takes you deeper—much deeper. Participants spend four full hours alongside researchers and trackers as they train a semi-wild gorilla family to become comfortable with human presence. This is not tourism—this is conservation in action, and you are an active participant.
                </p>

                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Only available in the Rushaga sector of Bwindi Impenetrable National Park, this exclusive experience allows just four visitors per day to join researchers from the Uganda Wildlife Authority and the Max Planck Institute. You will witness behaviors rarely seen by tourists, watch the family adapt to your presence in real time, and gain unprecedented insight into mountain gorilla society. This is the closest you can come to being a gorilla researcher without getting a PhD.
                </p>

                <h3 className="text-2xl font-bold text-safari-green mt-8 mb-4">What is Habituation?</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Habituation is the gradual process of training wild gorillas to tolerate human observers without changing their natural behavior. It typically takes 2-3 years of daily visits by researchers before a gorilla family becomes comfortable enough with humans to be visited by tourists. During this period, researchers document family structures, record individual personalities, assign names, and teach the gorillas that humans are not threats.
                </p>

                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  The families undergoing habituation are in various stages of this process—some are already fairly comfortable with humans, while others are still learning. This means you might witness gorillas displaying initial wariness, curiosity, or even bluff charges as they test boundaries. You will see the trackers and researchers employ specialized techniques to calm nervous gorillas, using soft vocalizations and submissive body language. It is conservation science happening before your eyes.
                </p>

                <h3 className="text-2xl font-bold text-safari-green mt-8 mb-4">Why Four Hours Changes Everything</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Standard gorilla trekking gives you one hour—enough time for introductions and a few behavioral observations. But gorilla families have complex social lives that unfold over hours, not minutes. Four hours allows you to witness the full arc of gorilla daily life: morning wake-up and grooming sessions, mid-morning foraging and feeding, playful juveniles wrestling, the silverback making decisions about where the family moves, midday rest periods, and afternoon feeding.
                </p>

                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  You will see personalities emerge. The protective mother who never lets her baby out of reach. The mischievous juvenile who tests the silverback's patience. The adolescent males practicing their chest-beating displays. The elderly female who mediates disputes. The silverback's subtle signals that direct family movements. In four hours, these gorillas transform from wildlife into individuals with stories, relationships, and distinct characters.
                </p>

                <h3 className="text-2xl font-bold text-safari-green mt-8 mb-4">A Day Unlike Any Other</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Your day begins before dawn with a briefing from lead researchers who explain the specific family you will visit, their habituation progress, and current behavioral patterns. Then you set off into the forest, often hiking for 2-4 hours to reach the gorillas' night nests. Trackers who monitored the family the previous day lead you to their location.
                </p>

                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  When you find them, they might still be in their nests—massive constructions of bent vegetation where gorillas sleep each night. You will watch them wake, stretch, and begin their day. Researchers take notes on every behavior, and you are encouraged to ask questions, observe techniques, and learn the science behind habituation. Unlike standard trekking where silence is mandatory, habituation allows for whispered conversations with researchers who share their extensive knowledge.
                </p>

                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Throughout the four hours, you will move with the family as they forage. Gorillas are surprisingly mobile, sometimes covering several kilometers. You will scramble through thick vegetation, slide down muddy slopes, and push through vines—following wherever the gorillas lead. It is physical, challenging, and utterly exhilarating. By the end, you will feel like a temporary member of the research team, having contributed observational data to ongoing studies.
                </p>

                <h3 className="text-2xl font-bold text-safari-green mt-8 mb-4">Conservation at the Cutting Edge</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Your permit fee funds critical conservation work, supporting researcher salaries, anti-poaching patrols, habituation programs that expand tourism capacity, and community development initiatives. More importantly, it demonstrates the economic value of living gorillas, giving local communities powerful incentives to protect rather than poach.
                </p>

                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Habituation also serves conservation by increasing the number of gorilla families available for tourism. Each newly habituated family creates eight more daily tourism slots, distributing visitor pressure across more groups and reducing stress on any single family. Your participation in habituation directly enables this expansion while funding the intensive work required.
                </p>

                <h3 className="text-2xl font-bold text-safari-green mt-8 mb-4">Who Should Do This?</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  The Gorilla Habituation Experience is not for everyone. It requires high physical fitness (expect 6-8 hours of strenuous hiking through difficult terrain), considerable endurance, and genuine interest in conservation science. If you want comfort and predictability, stick with standard trekking. But if you want the adventure of a lifetime—raw, challenging, and scientifically meaningful—this is your experience.
                </p>

                <p className="text-lg text-gray-700 leading-relaxed">
                  Wildlife photographers particularly love habituation. Four hours provides unlimited opportunities to capture behaviors, interactions, and expressions. The smaller group size (maximum four visitors versus eight for standard trekking) means less crowding and better angles. Many professional wildlife photographers consider the habituation experience essential for truly understanding and documenting mountain gorilla behavior.
                </p>
              </div>
            </section>

            {/* Activity Highlights */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-12 text-center">
                Why Choose the Habituation Experience
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                      <FontAwesomeIcon icon="fa-clock" className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-safari-green mb-2">Four Full Hours</h3>
                    <p className="text-gray-700">Spend 4 hours with gorillas versus 1 hour in standard trekking—witness complete behavioral cycles</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                      <FontAwesomeIcon icon="fa-microscope" className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-safari-green mb-2">Work with Researchers</h3>
                    <p className="text-gray-700">Join professional primatologists conducting habituation and behavioral studies</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                      <FontAwesomeIcon icon="fa-users" className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-safari-green mb-2">Exclusive Small Groups</h3>
                    <p className="text-gray-700">Maximum 4 visitors per family versus 8 for standard trekking—more intimate and personal</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                      <FontAwesomeIcon icon="fa-eye" className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-safari-green mb-2">Rare Behaviors</h3>
                    <p className="text-gray-700">Witness morning wake-ups, nest building, full feeding cycles, and social interactions</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                      <FontAwesomeIcon icon="fa-graduation-cap" className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-safari-green mb-2">Educational Deep Dive</h3>
                    <p className="text-gray-700">Learn habituation techniques, gorilla social structures, and conservation science firsthand</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                      <FontAwesomeIcon icon="fa-camera" className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-safari-green mb-2">Photographer Paradise</h3>
                    <p className="text-gray-700">Four hours provides unlimited photo opportunities in varying light and situations</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Sample Itinerary */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                Your Habituation Day – Sample Itinerary
              </h2>
              <div className="bg-safari-light border-l-4 border-safari-gold p-6 mb-8 rounded-r-lg">
                <p className="text-gray-700 text-lg">
                  <strong>⚠️ Important Note:</strong> This itinerary is tentative and fully customizable based on your preferences,
                  pickup location, and the specific gorilla family's location. We can adjust all aspects to suit your needs.
                </p>
              </div>
              <p className="text-center text-gray-700 mb-12 text-lg">
                Typically, your habituation day in Rushaga sector unfolds like this:
              </p>

              <div className="space-y-6">
                <div className="card">
                  <div className="p-6 flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 bg-safari-green rounded-full flex items-center justify-center text-white font-bold text-xl">
                        05:30
                      </div>
                    </div>
                    <div className="flex-grow">
                      <h3 className="text-xl font-bold text-safari-green mb-2">Pre-Dawn Preparation</h3>
                      <p className="text-gray-700">
                        Wake early for a substantial breakfast—you will need the energy! Dress in full trekking gear with multiple layers. Pack extra snacks and at least 3 liters of water. The day will be long and physically demanding.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="p-6 flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 bg-safari-green rounded-full flex items-center justify-center text-white font-bold text-xl">
                        06:30
                      </div>
                    </div>
                    <div className="flex-grow">
                      <h3 className="text-xl font-bold text-safari-green mb-2">Research Team Briefing</h3>
                      <p className="text-gray-700">
                        Meet your lead researcher at Rushaga sector headquarters. Learn about the specific gorilla family you will visit—their habituation progress, family dynamics, individual personalities, and current behavioral patterns. Researchers explain what to expect, how to interpret gorilla vocalizations, and what behaviors indicate stress versus comfort.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="p-6 flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 bg-safari-green rounded-full flex items-center justify-center text-white font-bold text-xl">
                        07:00
                      </div>
                    </div>
                    <div className="flex-grow">
                      <h3 className="text-xl font-bold text-safari-green mb-2">Trek to Night Nests</h3>
                      <p className="text-gray-700">
                        Follow trackers who monitored the gorillas the previous evening. The trek to their night nests can take 1-4 hours depending on where they slept. Terrain is steep, muddy, and covered in thick vegetation. Porters are highly recommended (and support local communities)—you'll want your hands free for balance and cameras.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="p-6 flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 bg-safari-gold rounded-full flex items-center justify-center text-white font-bold text-xl">
                        <FontAwesomeIcon icon="fa-star" className="w-8 h-8" />
                      </div>
                    </div>
                    <div className="flex-grow">
                      <h3 className="text-xl font-bold text-safari-green mb-2">Morning Wake-Up (Hours 1-2)</h3>
                      <p className="text-gray-700">
                        Arrive to find gorillas still in their nests or just beginning to stir. Watch them wake, stretch, groom each other, and demolish their night nests (which they never reuse). This intimate morning routine is something standard trekkers never see. Researchers point out individual gorillas, explain family relationships, and demonstrate how they use soft vocalizations to keep the gorillas calm. Take notes, ask questions, and observe scientific data collection.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="p-6 flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 bg-safari-gold rounded-full flex items-center justify-center text-white font-bold text-xl">
                        <FontAwesomeIcon icon="fa-star" className="w-8 h-8" />
                      </div>
                    </div>
                    <div className="flex-grow">
                      <h3 className="text-xl font-bold text-safari-green mb-2">Mid-Morning Foraging (Hours 2-3)</h3>
                      <p className="text-gray-700">
                        Follow the family as they begin serious feeding. Gorillas consume up to 30kg of vegetation daily, so they spend much of their time eating. Watch mothers teach infants which plants are edible, juveniles play-fight between feeding sessions, and the silverback make decisions about where the family moves. The researcher explains feeding patterns, nutritional requirements, and how habitat quality affects gorilla health. You will move constantly, scrambling through vines and sliding down slopes to keep up.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="p-6 flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 bg-safari-gold rounded-full flex items-center justify-center text-white font-bold text-xl">
                        <FontAwesomeIcon icon="fa-star" className="w-8 h-8" />
                      </div>
                    </div>
                    <div className="flex-grow">
                      <h3 className="text-xl font-bold text-safari-green mb-2">Midday Rest and Social Time (Hours 3-4)</h3>
                      <p className="text-gray-700">
                        Around midday, the family typically takes a rest period. This is when you see extensive grooming, juveniles wrestling, and infants playing. The silverback may rest nearby while keeping watch. This is prime photography time—gorillas are relaxed and engaged in social behaviors. Researchers explain social hierarchies, mating systems, and how silverbacks maintain family cohesion. By now, you have seen these gorillas through multiple behavioral contexts and they feel like individuals you know.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="p-6 flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 bg-safari-green rounded-full flex items-center justify-center text-white font-bold text-xl">
                        13:00
                      </div>
                    </div>
                    <div className="flex-grow">
                      <h3 className="text-xl font-bold text-safari-green mb-2">Return Trek and Debrief</h3>
                      <p className="text-gray-700">
                        After your four hours, say goodbye to your gorilla family and trek back to headquarters. The return typically takes 1-2 hours. At headquarters, enjoy a late lunch and receive your habituation certificate. Researchers debrief the session, sharing insights about what you observed and how it contributes to ongoing studies. Return to your lodge exhausted, exhilarated, and forever changed.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* FAQs */}
            <FAQSection faqs={faqs} title="Habituation Experience FAQs" />

            {/* What to Pack */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                What to Pack for Gorilla Habituation
              </h2>
              <p className="text-center text-gray-700 mb-12 text-lg">
                Pack the same gear as standard trekking, but with extra emphasis on endurance and photography:
              </p>

              <div className="grid md:grid-cols-2 gap-8">
                <div className="card">
                  <div className="p-6">
                    <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center mb-4">
                      <FontAwesomeIcon icon="fa-droplet" className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-safari-green mb-3">Extra Water and Snacks</h3>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start gap-2">
                        <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                        <span>Minimum 3 liters of water (full day)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                        <span>Energy bars, nuts, dried fruit</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                        <span>Packed lunch from your lodge</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                        <span>Electrolyte tablets or sports drinks</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="card">
                  <div className="p-6">
                    <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center mb-4">
                      <FontAwesomeIcon icon="fa-camera" className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-safari-green mb-3">Photography Gear</h3>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start gap-2">
                        <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                        <span>DSLR or mirrorless with fast lens</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                        <span>Multiple batteries (cold drains them)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                        <span>Multiple high-capacity memory cards</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                        <span>Lens cleaning cloth and waterproof bag</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="card">
                  <div className="p-6">
                    <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center mb-4">
                      <FontAwesomeIcon icon="fa-book" className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-safari-green mb-3">Note-Taking Materials</h3>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start gap-2">
                        <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                        <span>Waterproof notebook or field journal</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                        <span>Pencils (pens can freeze or leak)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                        <span>Phone or voice recorder for observations</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="card">
                  <div className="p-6">
                    <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center mb-4">
                      <FontAwesomeIcon icon="fa-shoe-prints" className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-safari-green mb-3">Durable Footwear</h3>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start gap-2">
                        <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                        <span>Well-broken-in waterproof hiking boots</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                        <span>Gaiters (highly recommended)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                        <span>Extra pair of dry socks</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="card">
                  <div className="p-6">
                    <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center mb-4">
                      <FontAwesomeIcon icon="fa-hand" className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-safari-green mb-3">Hand Protection</h3>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start gap-2">
                        <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                        <span>Gardening gloves (essential for vines)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                        <span>Hand sanitizer and wipes</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="card">
                  <div className="p-6">
                    <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center mb-4">
                      <FontAwesomeIcon icon="fa-pills" className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-safari-green mb-3">Medical Kit</h3>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start gap-2">
                        <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                        <span>Blister treatment supplies</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                        <span>Pain reliever and anti-inflammatory</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                        <span>Personal medications</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                        <span>Anti-diarrheal medication</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="mt-8 p-6 bg-white rounded-lg border-l-4 border-safari-gold shadow-sm">
                <p className="text-gray-700">
                  <strong className="text-safari-green">Pro Tip:</strong> Hire a porter without hesitation. They carry your pack, help you navigate difficult terrain, and are often former poachers now earning legal income from conservation. With 4 hours of gorilla time, you'll take hundreds of photos—bring multiple batteries, large memory cards, and waterproof protection. This is the wildlife photography opportunity of a lifetime!
                </p>
              </div>
            </section>

            {/* More Adventures */}
            <section>
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-4 text-center">
                More Adventures You'll Love
              </h2>
              <p className="text-center text-gray-700 mb-12 text-lg">
                Combine gorilla habituation with these amazing experiences:
              </p>

              <div className="grid md:grid-cols-2 gap-8">
                <Link to="/activities/gorilla-trekking" className="card group hover:shadow-xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1551033406-611cf9a28f67?w=800"
                      alt="Gorilla Trekking"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-safari-green mb-2">Gorilla Trekking</h3>
                    <p className="text-gray-700 mb-4">The classic 1-hour encounter with habituated gorillas. A perfect comparison to habituation.</p>
                    <p className="text-safari-gold font-bold">Permit fees apply – contact for details</p>
                  </div>
                </Link>

                <Link to="/activities/batwa-cultural-trail" className="card group hover:shadow-xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1523805009345-7448845a9e53?w=800"
                      alt="Batwa Trail"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-safari-green mb-2">Batwa Cultural Trail</h3>
                    <p className="text-gray-700 mb-4">Walk with the forest's original inhabitants and learn their ancient survival skills.</p>
                    <p className="text-safari-gold font-bold">Activity fees apply – contact us</p>
                  </div>
                </Link>
              </div>
            </section>
          </main>

          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="sticky top-24">
              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-4">Activity Details</h3>
                <div className="space-y-4 text-gray-700">
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-clock" className="w-6 h-6 text-safari-green" />
                    <span><strong>Duration:</strong> Full day (4 hours with gorillas)</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-calendar" className="w-6 h-6 text-safari-green" />
                    <span><strong>Best Time:</strong> Jun-Sep, Dec-Feb</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-dollar-sign" className="w-6 h-6 text-safari-green" />
                    <span><strong>Permit Fee:</strong> Contact for current rates</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-chart-simple" className="w-6 h-6 text-safari-green" />
                    <span><strong>Difficulty:</strong> Very Strenuous</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-users" className="w-6 h-6 text-safari-green" />
                    <span><strong>Group:</strong> Max 4 people</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-user" className="w-6 h-6 text-safari-green" />
                    <span><strong>Min Age:</strong> 15 years</span>
                  </div>
                </div>
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl w-full mt-6"
                >
                  Book Your Adventure
                </button>
              </div>

              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-check-circle" className="w-6 h-6 text-green-600" />
                  What's Included
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Gorilla habituation permit (mandatory)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">4 hours with gorillas (vs 1 hour standard)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Researchers and expert guides</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Park entry fees</span>
                  </li>
                </ul>
              </div>

              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-times-circle" className="w-6 h-6 text-red-600" />
                  What's Not Included
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Porter hire (optional but recommended)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Accommodation before and after</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Tips for guides and trackers</span>
                  </li>
                </ul>
              </div>

              <div className="card p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-heartbeat" className="w-6 h-6" />
                  Health & Fitness
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Fitness Level:</strong> High fitness required. 6-8 hours hiking in difficult terrain.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Medical Requirements:</strong> Yellow fever vaccination mandatory.</span>
                  </li>
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <BookingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        experience="Gorilla Habituation Experience - Rushaga, Bwindi"
      />
    </div>
  )
}
